/**
 * Az esemény pénzneme a panelen — egyetlen helyen (#111 utómunka).
 *
 * MIÉRT KÖZÖS. A pénznemet a védett `GET /events/:id/config` adja, mert a
 * publikus `GET /events` a #74 óta nem küldi a `pricing` szekciót. Ezt a
 * lekérést a panel négy helyen írta meg külön-külön — a Jegyek oldal, az
 * Események modál, a jegytípus-szerkesztő és a szponzorszint-szerkesztő —, és
 * mind a négy ugyanazzal a hibás tartalékkal. Egy ötödik hívó ugyanezt írta
 * volna meg ötödször.
 *
 * MI A LÉNYEGI KÜLÖNBSÉG a régi másolatokhoz képest: **az „ismeretlen" nem
 * ugyanaz, mint az „EUR".** A másolatok elhasalt lekérésre `'EUR'`-t adtak
 * vissza, tehát egy dinárban áruló esemény bevétele némán az euró-összegbe
 * került. Ez a modul `null`-t ad, és a hívó dönti el, mit kezd vele — a Jegyek
 * oldal külön, megjelölt vödörbe teszi, a szerkesztő űrlapok pedig a mai
 * megjelenítési tartalékot használják.
 */
import api from './api';

/**
 * Megjelenítési tartalék ott, ahol egy pénznemet MINDENKÉPP ki kell írni
 * (például egy üres bevétel-csempén). Nem az „ismeretlen" szinonimája.
 */
export const FALLBACK_CURRENCY = 'EUR';

/**
 * Egyszerre indított lekérések felső korlátja.
 *
 * A backend globális `short` korlátja **30 kérés / másodperc kliens-címenként**
 * (`backend-node-tsc-nestjs/src/common/throttle/throttle-profiles.ts`). Egy
 * `Promise.all` minden eseményre egyszerre lő, tehát 31 jegyes eseménytől a
 * kérések egy része 429-cel tér vissza — és mivel a hibaág korábban `'EUR'`-t
 * adott, a szervező ebből SEMMIT nem vett volna észre: a bevétel csak rossz
 * címkét kapott volna. Hatos csomagokkal jóval a korlát alatt maradunk, és ez
 * a panelen néhány tized másodperc.
 */
export const MAX_PARALLEL_CONFIG_READS = 6;

/**
 * Egy esemény pénzneme, vagy `null`, ha nem tudjuk.
 *
 * `null` a válasz akkor is, ha a lekérés elhasal (egy esemény-adminisztrátor
 * nem éri el mások eseményét → 403; sebességkorlát → 429), és akkor is, ha a
 * config nem tartalmaz pénznemet. A kettőt a hívó szempontjából nincs értelme
 * megkülönböztetni: mindkettő azt jelenti, hogy nem szabad számot címkézni.
 */
export async function fetchEventCurrency(eventId: string): Promise<string | null> {
  try {
    const { data } = await api.get(`/events/${eventId}/config`);
    const currency = data?.pricing?.currency;
    return typeof currency === 'string' && currency.trim() !== '' ? currency : null;
  } catch {
    return null;
  }
}

/**
 * Több esemény pénzneme, a sebességkorlát alatt maradva.
 *
 * A térképbe **csak a megtalált** pénznemek kerülnek be; amiről nem tudunk, az
 * hiányzik, nem pedig `'EUR'`-ként szerepel. Az ismétlődő azonosítókat egyszer
 * kérdezzük le.
 */
export async function fetchEventCurrencies(
  eventIds: readonly string[]
): Promise<Map<string, string>> {
  const unique = [...new Set(eventIds.filter(Boolean))];
  const found = new Map<string, string>();

  for (let i = 0; i < unique.length; i += MAX_PARALLEL_CONFIG_READS) {
    const batch = unique.slice(i, i + MAX_PARALLEL_CONFIG_READS);
    const results = await Promise.all(
      batch.map(async (id) => [id, await fetchEventCurrency(id)] as const)
    );
    for (const [id, currency] of results) {
      if (currency) found.set(id, currency);
    }
  }
  return found;
}

/**
 * Pénzösszeg kiírása.
 *
 * Ismeretlen pénznemnél **nem találunk ki egyet**: a szám pénznem-jel nélkül
 * marad, és a hívó teszi mellé a magyarázatot. Egy érvénytelen kódra az
 * `Intl.NumberFormat` dobna — az is ide fut be.
 */
export function formatMoney(amount: number, currency: string | null, locale: string): string {
  if (currency) {
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch {
      /* érvénytelen kód — a pénznem nélküli alakra esünk vissza */
    }
  }
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(amount);
}
